import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaLock, FaUser, FaEnvelope, FaExclamationCircle, FaPhone, FaMapMarkerAlt, FaBriefcase, FaCalendarAlt, FaGlobe } from "react-icons/fa";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Select } from "@/components/ui/select";
import { useAuth } from '../../contexts/AuthContext';

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    country: "",
    address: "",
    profession: "",
    dateOfBirth: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  
  const navigate = useNavigate();
  
  const { signup } = useAuth();
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const isFormValid = Object.values(formData).every(val => val.trim()) &&
    formData.password === formData.confirmPassword &&
    formData.password.length >= 6;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    // Validation: All fields required
    for (const key in formData) {
      if (!formData[key].trim()) {
        setError("All fields are required");
        return;
      }
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log("Attempting to register user...");
      // Create user profile object with emailVerified property set to false
      const userProfile = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        country: formData.country,
        address: formData.address,
        profession: formData.profession,
        dateOfBirth: formData.dateOfBirth,
        emailVerified: false
      };
      
      // Sign up user with Firebase
      await signup(formData.email, formData.password, userProfile);
      console.log("Registration successful, redirecting to verify-email");
      toast.success("Account has been created successfully. Please check your email for a confirmation.");
      
      // Ensure we're properly navigating to the verify email page
      setTimeout(() => {
        navigate('/auth/verify-email');
      }, 500);
    } catch (error: any) {
      console.error("Registration error:", error);
      setError(error.message || "Failed to create account");
      toast.error("Registration failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 py-2 md:py-4 px-2 md:px-8 lg:px-16">
      <div className="w-full max-w-2xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl space-y-8 px-2 md:px-8 lg:px-12">
        <div className="text-center">
          <img 
            src="/TRUTH NEXUS.svg" 
            alt="Truth Nexus Logo" 
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-truth">Truth Nexus Globe AI</h1>
          <p className="mt-2 text-gray-400">Create your account</p>
        </div>
        
        <Card className="bg-black/20 backdrop-blur-sm border-gray-800 min-h-0 overflow-visible">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Sign Up</CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Join our platform to combat misinformation
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && (
                <div className="p-3 bg-red-900/30 border border-red-800 rounded-md flex items-center gap-2 text-sm">
                  <FaExclamationCircle className="text-red-500" />
                  <span>{error}</span>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <FaUser className="text-gray-500" />
                    </div>
                    <Input 
                      id="firstName" 
                      name="firstName"
                      placeholder="John" 
                      value={formData.firstName}
                      onChange={handleChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <FaUser className="text-gray-500" />
                    </div>
                    <Input 
                      id="lastName" 
                      name="lastName"
                      placeholder="Doe" 
                      value={formData.lastName}
                      onChange={handleChange}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaEnvelope className="text-gray-500" />
                  </div>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="name@example.com" 
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <FaPhone className="text-gray-500" />
                    </div>
                    <Input 
                      id="phoneNumber" 
                      name="phoneNumber"
                      type="tel" 
                      placeholder="+1 (555) 000-0000" 
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <FaGlobe className="text-gray-500" />
                    </div>
                    <Input 
                      id="country" 
                      name="country"
                      placeholder="Your Country" 
                      value={formData.country}
                      onChange={handleChange}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaMapMarkerAlt className="text-gray-500" />
                  </div>
                  <Input 
                    id="address" 
                    name="address"
                    placeholder="Your Address" 
                    value={formData.address}
                    onChange={handleChange}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="profession">Profession</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <FaBriefcase className="text-gray-500" />
                    </div>
                    <Input 
                      id="profession" 
                      name="profession"
                      placeholder="Your Profession" 
                      value={formData.profession}
                      onChange={handleChange}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dateOfBirth">Date of Birth</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <FaCalendarAlt className="text-gray-500" />
                    </div>
                    <Input 
                      id="dateOfBirth" 
                      name="dateOfBirth"
                      type="date" 
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaLock className="text-gray-500" />
                  </div>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••" 
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <FaLock className="text-gray-500" />
                  </div>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="••••••••" 
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="terms"
                  className="rounded border-gray-700 bg-gray-800 text-truth focus:ring-truth"
                  required
                />
                <Label htmlFor="terms" className="text-sm">
                  I agree to the <Link to="/terms" className="text-truth hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-truth hover:underline">Privacy Policy</Link>
                </Label>
              </div>

              <div className="mt-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <h4 className="text-truth font-medium mb-2 text-base sm:text-lg">Privacy Notice</h4>
                <p className="text-xs sm:text-sm text-gray-400">
                  Your information is encrypted and stored securely. We do not share your data with third parties or use it for marketing purposes. 
                  Your privacy is our top priority, and we are committed to protecting your personal information in accordance with data protection regulations.
                </p>
              </div>
            </CardContent>
            
            <CardFooter className="flex flex-col space-y-4 pt-0 pb-2 md:pb-4">
              <Button 
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className={`w-full mt-4 ${!isFormValid || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? "Creating Account..." : "Create Account"}
              </Button>
              
              <p className="text-center text-sm text-gray-400">
                Already have an account?{" "}
                <Link to="/auth/signin" className="text-truth hover:underline">
                  Sign in
                </Link>
              </p>
            </CardFooter>
          </form>
        </Card>
        
        <div className="text-center">
          <Link to="/" className="text-sm text-gray-400 hover:text-truth">
            Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
